import { FC, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { SegmentListItem } from './SegmentListItem/SegmentListItem';
import { NewSegment } from './NewSegment';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { ISegment } from '../../../entities/offsiteCampaign';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../context/UseOffsiteCampaignEditorContext';
import { getCampaignSubState } from 'entities/lifecycle/activityState';
import { Box, theme } from '@salecycle/ui-component-library';
import Xarrow, { Xwrapper, useXarrow } from 'react-xarrows';

const GlobalSortingStyle = createGlobalStyle`
  .sorting {
    list-style-type: none;
    z-index: 11;
  }
`;

// background-color: ${({ theme }) => theme.vars.newColor.darkGS2};
const StyledSegments = styled.ul`
  overflow-x: auto;
  display: flex;
  flex-wrap: nowrap;
  padding: 4.5rem 4rem;
  background-color: ${({ theme }) => theme.vars.newColor.gold1};
  list-style-type: none;
  height: 100%;
  margin-bottom: 0;
  width: calc(100vw - 400px);
  ::-webkit-scrollbar {
    width: 10px;
    &-track {
      background-color: ${({ theme }) => theme.vars.newColor.darkGS3};
    }
    &-thumb {
      background-color: #fff;
      :hover {
        background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
      }
    }
  }
`;

const AddSegmentListItem = styled.li`
  min-width: max-content;
  margin-bottom: 6rem;
`;

const StyledSegmentListItem = styled.li`
  min-width: max-content;
  text-align: left;
`;

const segmentGap = 40;

const getXarrow = (end: string) =>
  (window as any).Cypress ? (
    <div aria-label="arrow"></div>
  ) : (
    <Xarrow
      start="addSegment-button"
      end={end}
      startAnchor="bottom"
      endAnchor="top"
      color={theme.vars.newColor.blue4}
      showHead={false}
      divContainerStyle={end === 'cloned-segment' ? {} : { position: 'relative', marginRight: -segmentGap }}
      divContainerProps={{ 'aria-label': 'arrow' }}
    />
  );

export const SortableSegment = SortableElement(
  ({ segment, sortIndex, status }: { segment: ISegment; sortIndex: number; status: CampaignState }) => (
    <>
      <StyledSegmentListItem id={`segment-${sortIndex}`} key={segment.id} data-testid="SegmentListItem">
        <SegmentListItem segment={segment} index={sortIndex} campaignState={status} />
      </StyledSegmentListItem>

      {document.getElementById(`segment-${sortIndex}`)?.style.visibility === 'hidden' ? (
        <div style={{ marginRight: -segmentGap }}></div>
      ) : (
        getXarrow(`segment-${sortIndex}`)
      )}
    </>
  ),
);

export const SortableSegmentList = SortableContainer(
  ({ segments, status }: { segments: ISegment[]; status: CampaignState }) => (
    <>
      <GlobalSortingStyle />
      <StyledSegments data-testid="SegmentList">
        <Xwrapper>
          <Box stackItems={true} horizontalPositioning="center">
            <AddSegmentListItem>
              <NewSegment />
            </AddSegmentListItem>
            <Box horizontalPositioning="space-between" gap={`${segmentGap}px`}>
              {segments.map((segment, index) => (
                <SortableSegment
                  key={`item-${segment.id}`}
                  index={index}
                  sortIndex={index}
                  segment={segment}
                  status={status}
                />
              ))}
            </Box>
          </Box>
        </Xwrapper>
      </StyledSegments>
    </>
  ),
);

const SegmentList: FC = () => {
  // Time to wait before updating xarrows in milliseconds. This is required to update the position of the arrows after the page has loaded.
  // This is based on the connection speed, but works with fast-3g onwards, so should be fine for now.
  const updateDelay = 200;

  const { campaign } = useOffsiteCampaignEditorState();
  const campaignState = getCampaignSubState(campaign);

  const dispatch = useOffsiteCampaignEditorDispatch();
  const updateArrow = useXarrow();

  const [isDragging, setIsDragging] = useState(false);

  const arrow = getXarrow('cloned-segment');

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setIsDragging(false);

    if (oldIndex === newIndex) {
      return;
    }
    dispatch({
      type: 'REORDER_SEGMENTS',
      payload: { oldIndex: oldIndex, newIndex: newIndex },
    });
  };

  const handleSortMove = () => {
    window.requestAnimationFrame(updateArrow);
  };

  const segments = useRef(campaign.segments.length);
  useEffect(() => {
    if (segments.current === campaign.segments.length) {
      return;
    }

    const timeout = setTimeout(() => {
      updateArrow();
      segments.current = campaign.segments.length;
    }, updateDelay);

    return () => clearTimeout(timeout);
  }, [updateArrow, campaign]);

  return (
    <>
      <SortableSegmentList
        segments={campaign.segments}
        axis="x"
        helperClass="sorting"
        useDragHandle={true}
        onSortEnd={onSortEnd}
        status={campaignState}
        onSortStart={() => {
          const clone = document.querySelector('.sorting');
          if (clone) {
            clone.id = 'cloned-segment';
            setIsDragging(true);
          }
        }}
        onSortMove={handleSortMove}
      />
      {isDragging && arrow}
    </>
  );
};

export default SegmentList;
